import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MaskLines, Reveal } from "../components/Reveal";
import { CONTACT } from "../data/site";
import { Socials } from "../components/Socials";
import { ArrowRight, Check, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Brand Strategy", "Branding & Visual Identity", "Advertising", "Digital Marketing",
  "Social Media Marketing", "Content Creation", "E-commerce", "Campaign Development", "PMO", "Other",
];

const empty = { name: "", email: "", phone: "", company: "", project_type: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-dark-text/55">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-dark-text/25 pb-3 font-sans text-base md:text-lg text-dark-text placeholder:text-dark-text/30 focus:border-bottle-green focus:outline-none transition-colors";

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-bottle-green text-cream pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="label text-cream/55">
            Let's work together
          </motion.span>
          <h1 className="mt-6">
            <MaskLines
              lines={["Have an idea?", "Let's make", "it matter."]}
              delay={0.2}
              lineClass="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-cream"
            />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="font-sans text-base md:text-lg text-cream/75 leading-relaxed max-w-2xl mt-8">
            Whether you're launching something new, redefining your brand, growing your digital presence or looking for a creative partner, we're ready to build something meaningful with you.
          </motion.p>
        </div>
      </section>

      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-14 md:gap-20">
          {/* Form */}
          <div className="md:col-span-7">
            {done ? (
              <Reveal className="border border-dark-text/15 rounded-sm p-10 md:p-14 bg-warm-beige/50">
                <div className="w-14 h-14 rounded-full bg-bottle-green flex items-center justify-center">
                  <Check className="w-6 h-6 text-cream" />
                </div>
                <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-dark-text mt-8">Message received.</h2>
                <p className="font-sans text-base text-dark-text/70 mt-4 leading-relaxed max-w-md">
                  Thank you for reaching out to Tara Creative Group. We've received your inquiry and will get back to you shortly.
                </p>
                <button data-testid="contact-send-another" onClick={() => setDone(false)} className="mt-8 font-mono text-xs tracking-[0.18em] uppercase text-bottle-green link-underline">
                  Send another message
                </button>
              </Reveal>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-9">
                <div className="grid sm:grid-cols-2 gap-9">
                  <Field label="Name *">
                    <input data-testid="contact-name" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="contact-email" type="email" className={inputCls} placeholder="you@company.com" value={form.email} onChange={set("email")} />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="contact-phone" className={inputCls} placeholder="+91 ..." value={form.phone} onChange={set("phone")} />
                  </Field>
                  <Field label="Company">
                    <input data-testid="contact-company" className={inputCls} placeholder="Company name" value={form.company} onChange={set("company")} />
                  </Field>
                </div>

                <Field label="Project Type">
                  <select data-testid="contact-project-type" className={`${inputCls} appearance-none cursor-pointer`} value={form.project_type} onChange={set("project_type")}>
                    <option value="">Select a service…</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>

                <Field label="Message *">
                  <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your project…" value={form.message} onChange={set("message")} />
                </Field>

                <button
                  type="button"
                  onClick={submit}
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-bottle-green text-cream font-sans text-sm tracking-wide uppercase rounded-sm hover:bg-secondary-green transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Start a Project"}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
                </button>
              </form>
            )}
          </div>

          {/* Details */}
          <div className="md:col-span-4 md:col-start-9">
            <Reveal>
              <span className="label text-dark-text/50">Tara Creative Group</span>
              <div className="mt-8 space-y-7">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
                  <Mail className="w-5 h-5 text-secondary-green mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span className="font-sans text-base text-dark-text/80 group-hover:text-bottle-green transition-colors break-all">{CONTACT.email}</span>
                </a>
                <a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone-link" className="flex items-start gap-4 group">
                  <Phone className="w-5 h-5 text-secondary-green mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span className="font-sans text-base text-dark-text/80 group-hover:text-bottle-green transition-colors">{CONTACT.phone}</span>
                </a>
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-secondary-green mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span className="font-sans text-base text-dark-text/70 leading-relaxed">{CONTACT.location}</span>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-dark-text/10">
                <span className="label text-dark-text/50">Follow</span>
                <div className="mt-5">
                  <Socials variant="boxDark" iconSize="w-4 h-4" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
